import { FC, useEffect, useRef, useState } from "react";
import Label from "./Label";
import QuestionMarkIcon from "public/question-mark.svg";
import { Tooltip2 } from "elements/Tooltip";

interface SearchableSelectProps {
	label?: string;
	labelEl?: FC;
	labelBeside?: boolean;
	containerClassName?: string;
	tooltipText?: string;
	value: string;
	onChange: (value: string) => void;
	options: { value: string; label: string }[];
	placeholder?: string;
	required?: boolean;
	noResultsText?: string;
}

const SearchableSelect: FC<SearchableSelectProps> = ({
	label,
	labelBeside = false,
	labelEl: UserLabelEl,
	containerClassName,
	required,
	value,
	onChange,
	options,
	placeholder,
	tooltipText,
	noResultsText,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedOption, setSelectedOption] = useState<{
		value: string;
		label: string;
	} | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const LabelEl = UserLabelEl || Label;

	useEffect(() => {
		const selected = options.find(opt => opt.value === value);
		setSelectedOption(selected || null);
	}, [value, options]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				if (
					searchTerm &&
					(!selectedOption || searchTerm !== selectedOption.label)
				) {
					setSearchTerm("");
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [searchTerm, selectedOption]);

	const filteredOptions = options.filter(option =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleSelect = (option: { value: string; label: string }) => {
		setSelectedOption(option);
		onChange(option.value);
		setIsOpen(false);
		setSearchTerm("");
	};

	const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
		e.stopPropagation();
		setIsOpen(true);
		if (inputRef.current) {
			inputRef.current.select();
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setIsOpen(true);
	};

	return (
		<div
			className={containerClassName}
			style={{
				display: "flex",
				flexDirection: labelBeside ? "row" : "column",
			}}>
			{label && (
				<div className="label-container">
					<LabelEl className="input-label" required={required}>
						{label}
					</LabelEl>
					{tooltipText && (
						<Tooltip2 label={label} text={tooltipText}>
							<QuestionMarkIcon />
						</Tooltip2>
					)}
				</div>
			)}
			<div className="select-field-input-wrapper" ref={dropdownRef}>
				<div
					className={`searchable-select ${isOpen ? "open" : ""} ${value ? "" : "empty"} ${label ? " with-label" : ""}`}
					onClick={() => setIsOpen(!isOpen)}>
					<input
						ref={inputRef}
						type="text"
						value={isOpen ? searchTerm : selectedOption?.label || ""}
						onChange={handleInputChange}
						onClick={handleInputClick}
						placeholder={placeholder}
						className="searchable-select-input"
					/>
					<div className="searchable-select-arrow" />
				</div>
				{isOpen && (
					<div className="searchable-select-dropdown">
						{filteredOptions.length > 0 ? (
							filteredOptions.map(option => (
								<div
									key={option.value}
									className={`searchable-select-option ${
										option.value === value ? "selected" : ""
									}`}
									onClick={() => handleSelect(option)}>
									{option.label}
								</div>
							))
						) : (
							<div className="searchable-select-no-results">
								{noResultsText || "No results found"}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default SearchableSelect;
